import { action, computed, makeObservable, observable } from 'mobx';

import { ApiError } from 'src/packages/errors';

import type { TDictionaryFieldUpdates, TUnitColumnBinding, TUnitSettingItem, TUnitsSettingsStatus } from './types';
import type { UnitsSettingsService } from './units-settings-service';
import type { TControlView } from 'src/services/directory-service/types';
import type { INotificationsService } from 'src/services/notifications-service';

import { createUnitColumnBindings, createUnitSettingItems } from './units-settings-mappers';

export class UnitsSettingsStore {
  readonly objectType: string;
  @observable.shallow private bindings: TUnitColumnBinding[] = [];
  @observable.ref private changedFields: TDictionaryFieldUpdates = {};
  private readonly rowErrors = observable.map<string, string | null>();

  constructor(
    objectType: string,
    private readonly service: UnitsSettingsService,
    private readonly notifications: INotificationsService,
    private readonly getIsEditingAvailable: () => boolean,
  ) {
    this.objectType = objectType;

    makeObservable(this);
  }

  @computed
  get isAvailable(): boolean {
    return this.getIsEditingAvailable() && this.status !== 'disabled';
  }

  @computed
  get status(): TUnitsSettingsStatus {
    if (!this.bindings.length) {
      return 'disabled';
    }

    return this.service.getState(this.objectType).status;
  }

  @computed
  get items(): TUnitSettingItem[] {
    const state = this.service.getState(this.objectType);

    if (!this.bindings.length || state.status !== 'ready' || !state.settings) {
      return [];
    }

    return createUnitSettingItems(this.bindings, state.settings, state.classesOfUnit, state.units).map((item) => {
      const changedField = this.changedFields[item.fieldId];
      const isSaving = !!changedField;
      const hasError = this.rowErrors.has(item.fieldId);

      return {
        ...item,
        displayValue: changedField?.uomId ?? item.value,
        isDisabled: item.isDisabled || isSaving,
        isSaving,
        hasError,
        errorMessage: this.rowErrors.get(item.fieldId) ?? null,
      };
    });
  }

  @action.bound
  initialize(fieldViews: TControlView[]): void {
    this.bindings = createUnitColumnBindings(fieldViews);

    if (!this.bindings.length) {
      return;
    }

    void this.service.initialize(this.objectType);
  }

  @action.bound
  retry(): void {
    if (this.status !== 'error' || !this.bindings.length) {
      return;
    }

    void this.service.initialize(this.objectType);
  }

  @action.bound
  changeUnit(fieldId: string, uomId: number): void {
    if (!this.getIsEditingAvailable()) {
      return;
    }

    const item = this.items.find((setting) => setting.fieldId === fieldId);

    if (
      !item ||
      item.isDisabled ||
      item.classOfUnitId === null ||
      !item.options.some((option) => option.value === uomId)
    ) {
      return;
    }

    if (item.value === uomId) {
      this.rowErrors.delete(fieldId);
      return;
    }

    this.rowErrors.delete(fieldId);
    this.changedFields = {
      ...this.changedFields,
      [fieldId]: { uomId, classOfUnitId: item.classOfUnitId },
    };

    void this.saveChangedFields();
  }

  private async saveChangedFields(): Promise<void> {
    const fields = { ...this.changedFields };

    if (!Object.keys(fields).length) {
      return;
    }

    try {
      const result = await this.service.patchFields(this.objectType, fields);

      if (result === 'aborted') {
        return;
      }

      this.clearSavedFields(fields);
    } catch (error) {
      console.error(`Failed to update unit settings for ${this.objectType}`, error);

      const errorMessage = error instanceof ApiError && error.message ? error.message : null;

      this.setRowsError(fields, errorMessage);

      if (errorMessage) {
        this.notifications.showErrorMessage(errorMessage);
      } else {
        this.notifications.showErrorMessageT('displaySettings:unitsSettings.saveError');
      }
    }
  }

  @action.bound
  private clearSavedFields(fields: TDictionaryFieldUpdates): void {
    this.changedFields = {};

    Object.keys(fields).forEach((fieldId) => {
      this.rowErrors.delete(fieldId);
    });
  }

  @action.bound
  private setRowsError(fields: TDictionaryFieldUpdates, errorMessage: string | null): void {
    this.changedFields = {};

    Object.keys(fields).forEach((fieldId) => {
      this.rowErrors.set(fieldId, errorMessage);
    });
  }

  getUnitAbbreviation(fieldId: string): string | null {
    const item = this.items.find((setting) => setting.fieldId === fieldId);

    if (!item || item.value === null) {
      return null;
    }

    return item.options.find((option) => option.value === item.value)?.label ?? null;
  }
}
